import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  FonteNotificacoes,
  NotificacaoCategoria,
  NotificacaoItem,
  NotificationBellState,
  NotificationTab,
} from '@/types/notificationBell/types';

import {
  dismissNotification,
  fetchNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from './actions';

const initialState: NotificationBellState = {
  fonte: null,
  items: [],
  loading: false,
  error: null,
  dropdownOpen: false,
  activeTab: 'unread',
  categoryFilter: null,
  pendingDismissals: {},
  pendingMarkAllSnapshot: null,
  pendingMarkAsRead: {},
};

const findIndex = (items: NotificacaoItem[], id: string): number =>
  items.findIndex((i) => i.id === id);

// Troca de caixa: o que está no estado é da outra fonte e não pode ser exibido nem acionado.
const assumirFonte = (state: NotificationBellState, fonte: FonteNotificacoes) => {
  if (state.fonte === fonte) return;
  state.fonte = fonte;
  state.items = [];
  state.categoryFilter = null;
  state.pendingDismissals = {};
  state.pendingMarkAllSnapshot = null;
  state.pendingMarkAsRead = {};
};

const slice = createSlice({
  name: 'notification-bell',
  initialState,
  reducers: {
    dropdownToggled: (state) => {
      state.dropdownOpen = !state.dropdownOpen;
    },
    dropdownClosed: (state) => {
      state.dropdownOpen = false;
    },
    tabChanged: (state, action: PayloadAction<NotificationTab>) => {
      state.activeTab = action.payload;
    },
    categoryFilterChanged: (state, action: PayloadAction<NotificacaoCategoria | null>) => {
      state.categoryFilter = action.payload;
    },
    itemRemoved: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((i) => i.id !== action.payload);
    },
  },
  // Toda resposta e toda ação otimista de uma fonte que não é a dos `items` é descartada: é o
  // fetch atrasado da caixa que a pessoa acabou de deixar.
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state, action) => {
        assumirFonte(state, action.meta.arg);
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        if (action.meta.arg !== state.fonte) return;
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        if (action.meta.arg !== state.fonte) return;
        state.loading = false;
        state.error = action.error.message ?? 'Erro desconhecido';
      })
      .addCase(markNotificationAsRead.pending, (state, action) => {
        const { id, fonte } = action.meta.arg;
        if (fonte !== state.fonte) return;
        const idx = findIndex(state.items, id);
        if (idx >= 0) {
          state.pendingMarkAsRead[id] = state.items[idx].lida;
          state.items[idx].lida = true;
        }
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        delete state.pendingMarkAsRead[action.meta.arg.id];
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        const { id, fonte } = action.meta.arg;
        if (fonte !== state.fonte) return;
        const previousLida = state.pendingMarkAsRead[id];
        const idx = findIndex(state.items, id);
        if (idx >= 0 && previousLida !== undefined) {
          state.items[idx].lida = previousLida;
        }
        delete state.pendingMarkAsRead[id];
      })
      .addCase(dismissNotification.pending, (state, action) => {
        const { id, fonte } = action.meta.arg;
        if (fonte !== state.fonte) return;
        const item = state.items.find((i) => i.id === id);
        if (item) {
          state.pendingDismissals[id] = item;
        }
        state.items = state.items.filter((i) => i.id !== id);
      })
      .addCase(dismissNotification.fulfilled, (state, action) => {
        delete state.pendingDismissals[action.meta.arg.id];
      })
      .addCase(dismissNotification.rejected, (state, action) => {
        const { id, fonte } = action.meta.arg;
        if (fonte !== state.fonte) return;
        const item = state.pendingDismissals[id];
        if (item) {
          state.items.push(item);
          delete state.pendingDismissals[id];
        }
      })
      .addCase(markAllNotificationsAsRead.pending, (state, action) => {
        if (action.meta.arg !== state.fonte) return;
        state.pendingMarkAllSnapshot = state.items.map((i) => ({ ...i }));
        state.items = state.items.map((i) => ({ ...i, lida: true }));
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state, action) => {
        if (action.meta.arg !== state.fonte) return;
        state.pendingMarkAllSnapshot = null;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        if (action.meta.arg !== state.fonte) return;
        if (state.pendingMarkAllSnapshot) {
          state.items = state.pendingMarkAllSnapshot;
          state.pendingMarkAllSnapshot = null;
        }
      });
  },
});

export const { dropdownToggled, dropdownClosed, tabChanged, categoryFilterChanged, itemRemoved } =
  slice.actions;
export default slice.reducer;
